import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  MantisWorkbenchActionView,
  MantisWorkbenchFindingView,
  MantisWorkbenchReceiptView,
  MantisWorkbenchStage,
  MantisWorkbenchStageState,
  PlatformHealthMantisResponse,
} from '@/pages/platform-health/types';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  RefreshCw,
  Search,
  ShieldCheck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

const REFRESH_INTERVAL_MS = 60_000;
const SUBSYSTEMS = [
  'all',
  'axon',
  'aquix',
  'raven',
  'pulse',
  'lattice',
  'prism',
  'vital',
  'mantis',
];
const LIFECYCLES = [
  'all',
  'unhandled',
  'no_action',
  'routed',
  'delivering',
  'delivered',
  'owned',
  'investigating',
  'remediating',
  'revalidating',
  'recovered',
  'failed',
];
const SLA_STATES = ['all', 'overdue', 'due', 'on_track', 'none'];
const ACTION_KINDS = [
  'all',
  'notify',
  'ticket',
  'investigate',
  'escalate',
  'revalidate',
  'request_authorization',
  'remediate',
];
const IMPACTS = ['all', 'release_blocking', 'customer_visible', 'operator_action', 'informational'];
const STAGES: MantisWorkbenchStage[] = [
  'routed',
  'delivered',
  'owned',
  'investigated',
  'remediated',
  'revalidated',
];

function display(value: string) {
  return value.replace(/_/g, ' ');
}

function timestamp(value: string | null) {
  return value ? new Date(value).toLocaleString() : '—';
}

function seconds(value: number | null) {
  if (value === null) return '—';
  if (value < 3600) return `${Math.round(value / 60)} min`;
  if (value < 86_400) return `${(value / 3600).toFixed(1)} h`;
  return `${(value / 86_400).toFixed(1)} d`;
}

function stageTone(state: MantisWorkbenchStageState) {
  if (state === 'complete') return 'green';
  if (state === 'failed') return 'red';
  if (state === 'pending') return 'amber';
  if (state === 'unknown') return 'red';
  return 'neutral';
}

function lifecycleTone(finding: MantisWorkbenchFindingView) {
  if (finding.prominent || finding.lifecycle === 'failed' || finding.lifecycle === 'unhandled')
    return 'red';
  if (finding.lifecycle === 'recovered') return 'green';
  if (finding.lifecycle === 'no_action') return 'neutral';
  return 'blue';
}

function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: string }) {
  const colors: Record<string, string> = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
    green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  };
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        colors[tone] ?? colors.neutral
      )}
    >
      {children}
    </span>
  );
}

function Metric({
  label,
  value,
  warning = false,
}: {
  label: string;
  value: number;
  warning?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800">
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums text-gray-950 dark:text-white',
          warning && value > 0 && 'text-red-700 dark:text-red-300'
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function Receipt({ receipt }: { receipt: MantisWorkbenchReceiptView }) {
  return (
    <li
      data-testid="mantis-receipt"
      className="rounded bg-gray-50 p-2 text-[11px] text-gray-700 dark:bg-zinc-900 dark:text-gray-200"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={receipt.status === 'accepted' ? 'green' : 'red'}>{receipt.status}</Badge>
        <Badge>{receipt.system}</Badge>
        <Badge tone={receipt.verifiedBy === 'provider_api' ? 'green' : 'amber'}>
          {receipt.verifiedBy === 'provider_api' ? 'provider verified' : 'unverified'}
        </Badge>
        <span>{timestamp(receipt.observedAt)}</span>
      </div>
      <p className="mt-1">
        actor {receipt.recordedActorId ?? 'not recorded'} · external {receipt.externalId ?? '—'}
        {receipt.uri ? (
          <>
            {' · '}
            <a
              href={receipt.uri}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              open
            </a>
          </>
        ) : null}
      </p>
      <p className="mt-1 break-words font-mono text-[10px] text-gray-500">{receipt.detail}</p>
    </li>
  );
}

function ActionRow({ action }: { action: MantisWorkbenchActionView }) {
  return (
    <li className="rounded border border-gray-200 bg-white p-2 text-xs dark:border-zinc-700 dark:bg-zinc-800">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{display(action.kind)}</span>
        <Badge
          tone={
            action.lifecycle === 'failed' || action.lifecycle === 'remediation_failed'
              ? 'red'
              : action.lifecycle === 'delivered' || action.lifecycle === 'recovered'
                ? 'green'
                : 'amber'
          }
        >
          {display(action.lifecycle)}
        </Badge>
        {!action.recorded ? <Badge tone="red">not recorded</Badge> : null}
        {action.superseded ? <Badge>superseded</Badge> : null}
        <Badge>{action.authorization.class}</Badge>
        {action.authorization.requested ? (
          <Badge tone="red">
            {action.authorization.requested.requiredClass} ·{' '}
            {action.authorization.requested.grantCheck ?? 'no grant'}
          </Badge>
        ) : null}
        <span className="text-gray-500">
          → {action.destination.kind}:{action.destination.ref} · {display(action.urgency)} · not
          before {timestamp(action.notBefore)}
        </span>
      </div>
      <p className="mt-1 font-mono text-[10px] text-gray-500">{action.actionId}</p>
      <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-300">
        {action.attempts.length} attempt{action.attempts.length === 1 ? '' : 's'} ·{' '}
        {action.receipts.length} receipt{action.receipts.length === 1 ? '' : 's'}
        {action.problems.length ? ` · problems: ${action.problems.join(', ')}` : ''}
      </p>
      {action.receipts.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {action.receipts.map((receipt) => (
            <Receipt key={receipt.receiptId} receipt={receipt} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function FindingDetail({ finding }: { finding: MantisWorkbenchFindingView }) {
  return (
    <div
      data-testid="mantis-finding-detail"
      className="border-t border-gray-100 bg-gray-50/70 px-4 py-4 dark:border-zinc-700 dark:bg-zinc-900/40"
    >
      <section data-testid="mantis-chain" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
        {STAGES.map((stage) => {
          const view = finding.chain[stage];
          return (
            <div
              key={stage}
              className="rounded border border-gray-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-800"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                  {stage}
                </span>
                <Badge tone={stageTone(view.state)}>{display(view.state)}</Badge>
              </div>
              <p className="mt-1 text-[11px] text-gray-600 dark:text-gray-300">{view.detail}</p>
              <p className="mt-1 text-[10px] text-gray-500">{timestamp(view.since)}</p>
            </div>
          );
        })}
      </section>

      {finding.obligations.length > 0 ? (
        <section className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
          <h3 className="font-semibold">Obligations</h3>
          <ul className="mt-1 list-disc pl-5">
            {finding.obligations.map((obligation) => (
              <li key={`${obligation.kind}:${obligation.actionId ?? ''}:${obligation.since}`}>
                <strong>{display(obligation.kind)}</strong> since {timestamp(obligation.since)} —{' '}
                {obligation.detail}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <section className="rounded border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
            Routing and ownership
          </h3>
          <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <dt className="text-gray-500">Decision</dt>
            <dd>
              {finding.decision
                ? `${finding.decision.outcome} · ${finding.decision.winningRuleId}${finding.coordinatorRecorded ? '' : ' (not recorded in the ledger)'}`
                : 'none'}
            </dd>
            <dt className="text-gray-500">Owner</dt>
            <dd>
              {finding.owner?.displayName ?? 'unknown'}
              {finding.escalatedToOwnerId ? ` → escalated to ${finding.escalatedToOwnerId}` : ''}
            </dd>
            <dt className="text-gray-500">Acknowledgement</dt>
            <dd>
              {finding.acknowledgement.active
                ? `${finding.acknowledgement.active.acknowledgedBy} until ${timestamp(finding.acknowledgement.active.expiresAt)}`
                : `none active (${finding.acknowledgement.expiredCount} expired)`}
            </dd>
            <dt className="text-gray-500">Escalation deadline</dt>
            <dd>{timestamp(finding.escalationDeadline)}</dd>
            <dt className="text-gray-500">Epoch</dt>
            <dd>
              {finding.epochIndex ?? '—'}
              {finding.reopened
                ? ` · reopened ${finding.recurrence?.reopenCount ?? 0}× (last ${timestamp(finding.recurrence?.lastReopenedAt ?? null)})`
                : ''}
            </dd>
            <dt className="text-gray-500">Prior epochs</dt>
            <dd>
              {finding.priorEpochs.epochCount} epoch(s) · {finding.priorEpochs.actionCount} actions
              · {finding.priorEpochs.attemptCount} attempts · {finding.priorEpochs.receiptCount}{' '}
              receipts
              {finding.priorEpochs.obligations.length
                ? ` · ${finding.priorEpochs.obligations.length} stale obligation(s)`
                : ''}
            </dd>
          </dl>
          {finding.escalations.length > 0 ? (
            <ul className="mt-2 text-[11px] text-gray-600 dark:text-gray-300">
              {finding.escalations.map((escalation) => (
                <li key={escalation.escalationId}>
                  escalated ({display(escalation.reason)}) to {escalation.toOwnerId} at{' '}
                  {timestamp(escalation.occurredAt)}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
        <section className="rounded border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
            Investigation, remediation, revalidation
          </h3>
          <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <dt className="text-gray-500">Investigation</dt>
            <dd>
              {finding.investigation.results.length
                ? finding.investigation.results
                    .map((r) => `${r.verdict} (${r.sessionRef}, ${timestamp(r.completedAt)})`)
                    .join('; ')
                : 'no result recorded'}
            </dd>
            <dt className="text-gray-500">Remediation</dt>
            <dd>
              <Badge
                tone={
                  ['authorization_refused', 'still_failing', 'expired', 'error'].includes(
                    finding.remediation.stage
                  )
                    ? 'red'
                    : finding.remediation.stage === 'recovered'
                      ? 'green'
                      : finding.remediation.stage === 'none'
                        ? 'neutral'
                        : 'amber'
                }
              >
                {display(finding.remediation.stage)}
              </Badge>{' '}
              {finding.remediation.detail}
            </dd>
            <dt className="text-gray-500">Revalidation</dt>
            <dd>
              {finding.revalidation.revalidationId
                ? `${finding.revalidation.latestOutcome ?? 'waiting'} · ${finding.revalidation.trigger} · requested ${timestamp(finding.revalidation.requestedAt)} · expires ${timestamp(finding.revalidation.expiresAt)}${finding.revalidation.latestExplanation ? ` — ${finding.revalidation.latestExplanation}` : ''}`
                : 'none requested'}
            </dd>
          </dl>
        </section>
      </div>

      <section className="mt-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
          Actions, attempts and receipts
        </h3>
        {finding.actions.length > 0 ? (
          <ul className="mt-2 space-y-2">
            {finding.actions.map((action) => (
              <ActionRow key={action.actionId} action={action} />
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            No action has been planned for this finding.
          </p>
        )}
      </section>

      <div className="mt-4 flex flex-wrap gap-3 text-xs">
        <a
          href={`/admin/platform-health/critical?finding=${encodeURIComponent(finding.findingId)}`}
          className="font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          Open AXON finding
        </a>
        <span className="text-gray-500">
          check {finding.checkId} · rule {finding.ruleId} · subject {finding.subject.kind}:
          {finding.subject.id}
        </span>
      </div>
      <p className="mt-3 break-all text-[10px] text-gray-500 dark:text-gray-400">
        Evidence references: {finding.evidence.evidenceReferenceIds.join(', ') || 'none'} ·
        correlation groups: {finding.correlationGroupIds.join(', ') || 'none'}
      </p>
    </div>
  );
}

export function MantisWorkbenchContent({
  payload,
  selectedFindingId = null,
  onSelectFinding,
}: {
  payload: PlatformHealthMantisResponse;
  selectedFindingId?: string | null;
  onSelectFinding?: (findingId: string | null) => void;
}) {
  const { workbench } = payload;
  const signals = workbench.selfHealth.signals;
  const selfHealthFailed = workbench.selfHealth.outcome === 'fail';
  return (
    <div className="space-y-4">
      {!payload.available ? (
        <div
          data-testid="mantis-unavailable"
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100"
        >
          <strong>MANTIS response state is unavailable.</strong> A ledger or findings read failed;
          absence of a failure here is not an all-clear.
        </div>
      ) : null}
      {payload.partial ? (
        <div
          data-testid="mantis-partial"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <strong>Partial or inconsistent evidence.</strong>
          <ul className="mt-2 list-disc pl-5 text-xs">
            {payload.gaps.map((gap, index) => (
              <li key={`${gap.code}:${index}`}>{gap.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Metric label="Findings" value={workbench.summary.totalFindings} />
        <Metric label="Prominent" value={workbench.summary.prominent} warning />
        <Metric label="Unhandled" value={workbench.summary.unrecorded} warning />
        <Metric label="Overdue" value={workbench.summary.bySla.overdue} warning />
        <Metric label="Complete" value={workbench.summary.complete} />
        <Metric label="Incomplete" value={workbench.summary.incomplete} warning />
        <Metric label="Recovered" value={workbench.summary.byLifecycle.recovered} />
        <Metric label="Ledger records" value={workbench.source.recordCount} />
      </dl>
      <div className="grid gap-3 lg:grid-cols-2">
        <section
          data-testid="mantis-self-health"
          className={cn(
            'rounded-lg border p-4 text-sm',
            selfHealthFailed
              ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100'
              : workbench.selfHealth.completeness === 'partial'
                ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
                : 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100'
          )}
        >
          <div className="flex items-center gap-2 font-semibold">
            <ShieldCheck className="h-4 w-4" aria-hidden="true" /> MANTIS self-health:{' '}
            {workbench.selfHealth.outcome} / {workbench.selfHealth.completeness}
          </div>
          <p className="mt-1 text-xs">
            {workbench.selfHealth.reasons.length
              ? workbench.selfHealth.reasons.join(', ')
              : 'no failing or degrading signal'}
          </p>
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] sm:grid-cols-3">
            <dt>Queue lag</dt>
            <dd>
              {signals.queue_lag.count} (oldest {seconds(signals.queue_lag.oldestSeconds)})
            </dd>
            <dt>Transport failures</dt>
            <dd>{signals.transport_failure.count}</dd>
            <dt>Stuck AHC</dt>
            <dd>
              {signals.ahc_stuck.count} (oldest {seconds(signals.ahc_stuck.oldestSeconds)})
            </dd>
            <dt>Revalidation backlog</dt>
            <dd>{signals.revalidation_backlog.count}</dd>
            <dt>Coordinator recorded</dt>
            <dd>
              {signals.coordinator_never_recorded.failing
                ? 'never'
                : `${signals.coordinator_never_recorded.decisionCount} decisions`}
            </dd>
            <dt>Ledger</dt>
            <dd>
              {workbench.ledgerDurable ? 'durable' : 'not durable'} · {workbench.source.ledger}
            </dd>
          </dl>
        </section>
        <section
          data-testid="mantis-axon-parity"
          className={cn(
            'rounded-lg border p-4 text-sm',
            payload.axonComparison.matches
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100'
              : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
          )}
        >
          <div className="flex items-center gap-2 font-semibold">
            <Activity className="h-4 w-4" aria-hidden="true" /> AXON parity:{' '}
            {payload.axonComparison.matches ? 'matched' : 'unverified or mismatched'}
          </div>
          <p className="mt-1 text-xs">
            AXON active findings {payload.axonComparison.axonActiveFindings ?? 'unknown'} · MANTIS{' '}
            {payload.axonComparison.workbenchActiveFindings} · correlation groups{' '}
            {workbench.summary.correlationGroups} · materialization{' '}
            {workbench.materializationDigest.slice(0, 18)}
          </p>
        </section>
      </div>
      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
        <div className="border-b border-gray-200 px-4 py-3 text-xs text-gray-500 dark:border-zinc-700 dark:text-gray-400">
          Showing {workbench.summary.filteredFindings} of {workbench.summary.totalFindings} findings
          · prominent first · {workbench.summary.coordinatorRecorded} recorded by the coordinator
        </div>
        {workbench.findings.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">No findings match these filters.</p>
        ) : (
          workbench.findings.map((finding) => {
            const selected = selectedFindingId === finding.findingId;
            const tone = lifecycleTone(finding);
            const Icon =
              tone === 'green' ? CheckCircle2 : tone === 'red' ? AlertTriangle : Activity;
            return (
              <details
                key={finding.findingId}
                data-testid="mantis-finding-row"
                data-finding-id={finding.findingId}
                data-prominent={finding.prominent ? 'true' : 'false'}
                open={selected || undefined}
                onToggle={(event) => {
                  if (!onSelectFinding) return;
                  const open = event.currentTarget.open;
                  if (open && !selected) onSelectFinding(finding.findingId);
                  else if (!open && selected) onSelectFinding(null);
                }}
                className="group border-b border-gray-200 last:border-b-0 dark:border-zinc-700"
              >
                <summary className="grid cursor-pointer list-none gap-3 px-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:items-center">
                  <Icon
                    className={cn(
                      'h-5 w-5',
                      tone === 'green'
                        ? 'text-emerald-600'
                        : tone === 'red'
                          ? 'text-red-600'
                          : tone === 'blue'
                            ? 'text-blue-600'
                            : 'text-gray-400'
                    )}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-950 dark:text-white">
                        {finding.summary}
                      </span>
                      <Badge tone={tone}>{display(finding.lifecycle)}</Badge>
                      <Badge>{finding.subsystemId}</Badge>
                      <Badge
                        tone={
                          finding.severity === 'critical' || finding.severity === 'high'
                            ? 'red'
                            : 'neutral'
                        }
                      >
                        {finding.severity}
                      </Badge>
                      <Badge
                        tone={
                          finding.slaState === 'overdue'
                            ? 'red'
                            : finding.slaState === 'due'
                              ? 'amber'
                              : 'neutral'
                        }
                      >
                        {display(finding.slaState)}
                      </Badge>
                      {finding.prominentReasons.map((reason) => (
                        <Badge key={reason} tone="red">
                          {display(reason)}
                        </Badge>
                      ))}
                      {finding.reopened ? <Badge tone="amber">reopened</Badge> : null}
                      {!finding.coordinatorRecorded ? <Badge tone="red">not recorded</Badge> : null}
                    </div>
                    <p className="mt-1 truncate font-mono text-[11px] text-gray-500">
                      {finding.findingId}
                    </p>
                    <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                      {STAGES.map((stage) => (
                        <span key={stage}>
                          {stage}: {display(finding.chain[stage].state)}
                        </span>
                      ))}
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <p>{finding.owner?.displayName ?? 'owner unknown'}</p>
                    <p>{display(finding.impact)}</p>
                  </div>
                  <ChevronRight
                    className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-90"
                    aria-hidden="true"
                  />
                </summary>
                <FindingDetail finding={finding} />
              </details>
            );
          })
        )}
      </section>
    </div>
  );
}

function SelectFilter({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="text-xs text-gray-600 dark:text-gray-300">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
      >
        {values.map((item) => (
          <option key={item} value={item}>
            {display(item)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function MantisWorkbenchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const environment = searchParams.get('environment') ?? '';
  const subsystem = searchParams.get('subsystem') ?? 'all';
  const impact = searchParams.get('impact') ?? 'all';
  const lifecycle = searchParams.get('lifecycle') ?? 'all';
  const sla = searchParams.get('sla') ?? 'all';
  const actionKind = searchParams.get('actionKind') ?? 'all';
  const prominent = searchParams.get('prominent') ?? 'all';
  const owner = searchParams.get('owner') ?? '';
  const search = searchParams.get('search') ?? '';
  const selectedFindingId = searchParams.get('finding');
  const query = useQuery({
    queryKey: [
      'platform-health-mantis',
      environment,
      subsystem,
      impact,
      lifecycle,
      sla,
      actionKind,
      prominent,
      owner,
      search,
    ],
    queryFn: () =>
      api.getPlatformHealthMantis({
        environment: environment || undefined,
        subsystem: subsystem === 'all' ? undefined : subsystem,
        impact: impact === 'all' ? undefined : impact,
        lifecycle: lifecycle === 'all' ? undefined : lifecycle,
        sla: sla === 'all' ? undefined : sla,
        actionKind: actionKind === 'all' ? undefined : actionKind,
        prominent: prominent === 'prominent' ? 'true' : undefined,
        owner: owner || undefined,
        search: search || undefined,
      }),
    refetchInterval: REFRESH_INTERVAL_MS,
    retry: false,
  });

  function setParam(name: string, value: string | null, defaultValue?: string) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(name);
    else next.set(name, value);
    if (name !== 'finding') next.delete('finding');
    setSearchParams(next);
  }

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="MANTIS Response"
        subtitle="Was every actionable finding routed, delivered, owned, investigated, remediated and revalidated — and where did it stop"
      />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {query.data?.data.workbench.generatedAt
            ? `Environment ${query.data.data.workbench.environment} · updated ${timestamp(query.data.data.workbench.generatedAt)}`
            : 'Loading response evidence…'}
        </p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5', query.isFetching && 'animate-spin')}
            aria-hidden="true"
          />
          Refresh
        </button>
      </div>
      <section
        aria-label="MANTIS filters"
        className="mb-4 rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
      >
        <div className="flex flex-wrap gap-2">
          <label className="relative min-w-52 flex-1">
            <span className="sr-only">Search findings</span>
            <Search
              className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
            <input
              aria-label="Search findings"
              value={search}
              onChange={(event) => setParam('search', event.target.value)}
              placeholder="Finding, check, rule, subject or summary"
              className="h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
            />
          </label>
          <SelectFilter
            label="Environment"
            value={environment || 'default'}
            values={['default', 'production', 'staging', 'ci', 'development', 'unknown']}
            onChange={(value) => setParam('environment', value, 'default')}
          />
          <SelectFilter
            label="Subsystem"
            value={subsystem}
            values={SUBSYSTEMS}
            onChange={(value) => setParam('subsystem', value, 'all')}
          />
          <SelectFilter
            label="Impact"
            value={impact}
            values={IMPACTS}
            onChange={(value) => setParam('impact', value, 'all')}
          />
          <SelectFilter
            label="Lifecycle"
            value={lifecycle}
            values={LIFECYCLES}
            onChange={(value) => setParam('lifecycle', value, 'all')}
          />
          <SelectFilter
            label="SLA"
            value={sla}
            values={SLA_STATES}
            onChange={(value) => setParam('sla', value, 'all')}
          />
          <SelectFilter
            label="Action kind"
            value={actionKind}
            values={ACTION_KINDS}
            onChange={(value) => setParam('actionKind', value, 'all')}
          />
          <SelectFilter
            label="Prominence"
            value={prominent}
            values={['all', 'prominent']}
            onChange={(value) => setParam('prominent', value, 'all')}
          />
          <input
            aria-label="Owner"
            value={owner}
            onChange={(event) => setParam('owner', event.target.value)}
            placeholder="Owner ID"
            className="h-9 w-28 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
          />
        </div>
      </section>
      {query.isError ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          MANTIS workbench request failed. No response-completeness conclusion can be drawn.
        </div>
      ) : query.data ? (
        <MantisWorkbenchContent
          payload={query.data.data}
          selectedFindingId={selectedFindingId}
          onSelectFinding={(findingId) => setParam('finding', findingId)}
        />
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-800">
          Loading MANTIS ledger and findings…
        </div>
      )}
    </div>
  );
}
